import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useDeleteOpportunity,
  useOpportunity,
  useResearchOpportunity,
  useUpdateOpportunity,
} from "@/hooks/useOpportunities";
import { getOpportunityIntel } from "@/api/opportunities";
import { listSequences, enrollInSequence } from "@/api/sequences";
import { listLeads } from "@/api/leads";
import {
  createEngagement,
  listEngagements,
  syncOpportunityEmail,
} from "@/api/engagements";
import { syncEmails } from "@/api/emails";
import {
  listGoogleCalendarEvents,
  type GoogleCalendarEvent,
} from "@/api/google-calendar";
import { listDealPipelines } from "@/api/deal-pipelines";
import { BuyingCommittee } from "@/components/opportunities/buying-committee";
import { useSoftphone } from "@/components/calls/softphone-context";
import { createCall, completeCall } from "@/api/calls";
import { cn } from "@/cn";

const MEDDICC_FIELDS = [
  ["metrics", "Metrics"],
  ["economicBuyer", "Economic buyer"],
  ["decisionCriteria", "Decision criteria"],
  ["decisionProcess", "Decision process"],
  ["identifyPain", "Identify pain"],
  ["champion", "Champion"],
  ["competition", "Competition"],
] as const;

function formatMoney(amount?: number, currency?: string): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return String(amount);
  }
}

export function OpportunitiesDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const softphone = useSoftphone();
  const { data: deal, isLoading, error } = useOpportunity(id);
  const updateDeal = useUpdateOpportunity(id ?? "");
  const deleteDeal = useDeleteOpportunity();
  const researchJob = useResearchOpportunity();

  const pipelinesQuery = useQuery({
    queryKey: ["deal-pipelines"],
    queryFn: listDealPipelines,
    staleTime: 60_000,
  });

  const dealPipeline =
    pipelinesQuery.data?.pipelines.find(
      (p) => p.pipelineConfigId === deal?.pipelineConfigId,
    ) ??
    pipelinesQuery.data?.pipelines.find((p) => p.isDefault) ??
    pipelinesQuery.data?.pipelines[0];

  const stages = dealPipeline?.stages ?? [];

  const { data: intel } = useQuery({
    queryKey: ["opportunities", "intel", id],
    queryFn: () => getOpportunityIntel(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const { data: engagements = [] } = useQuery({
    queryKey: ["engagements", id],
    queryFn: () => listEngagements({ opportunityId: id!, limit: 50 }),
    enabled: !!id,
    staleTime: 15_000,
  });

  const { data: calendarData } = useQuery({
    queryKey: ["calendar", "events", id],
    queryFn: () =>
      listGoogleCalendarEvents({
        opportunityId: id!,
        createEngagement: true,
      }),
    enabled: !!id,
    staleTime: 60_000,
    retry: false,
  });
  const calendarEvents: GoogleCalendarEvent[] = calendarData?.events ?? [];

  const [dealName, setDealName] = useState("");
  const [amount, setAmount] = useState("");
  const [probability, setProbability] = useState("");
  const [forecastCategory, setForecastCategory] = useState("");
  const [closeDate, setCloseDate] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [nextStepDueAt, setNextStepDueAt] = useState("");
  const [winLossReason, setWinLossReason] = useState("");
  const [stage, setStage] = useState("");
  const [brief, setBrief] = useState("");
  const [meddicc, setMeddicc] = useState<Record<string, string>>({});
  const [noteDraft, setNoteDraft] = useState("");
  const [emailDraft, setEmailDraft] = useState("");
  const [callDraft, setCallDraft] = useState("");
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollSequenceId, setEnrollSequenceId] = useState("");
  const [intelOpen, setIntelOpen] = useState(false);

  useEffect(() => {
    if (!deal) return;
    setDealName(deal.dealName ?? "");
    setAmount(deal.amount != null ? String(deal.amount) : "");
    setProbability(deal.probability != null ? String(deal.probability) : "");
    setForecastCategory(deal.forecastCategory ?? "");
    setCloseDate(deal.closeDate ? deal.closeDate.slice(0, 10) : "");
    setOwnerName(deal.ownerName ?? "");
    setNextStep(deal.nextStep ?? "");
    setNextStepDueAt(deal.nextStepDueAt ? deal.nextStepDueAt.slice(0, 10) : "");
    setWinLossReason(deal.winLossReason ?? deal.rejectionReason ?? "");
    setStage(deal.stage ?? "");
    setBrief(deal.brief ?? "");
    setMeddicc({
      metrics: deal.meddicc?.metrics ?? "",
      economicBuyer: deal.meddicc?.economicBuyer ?? "",
      decisionCriteria: deal.meddicc?.decisionCriteria ?? "",
      decisionProcess: deal.meddicc?.decisionProcess ?? "",
      identifyPain: deal.meddicc?.identifyPain ?? "",
      champion: deal.meddicc?.champion ?? "",
      competition: deal.meddicc?.competition ?? "",
    });
  }, [deal]);

  const sequencesQuery = useQuery({
    queryKey: ["sequences"],
    queryFn: () => listSequences(),
    enabled: enrollOpen,
  });

  const dealContactsQuery = useQuery({
    queryKey: ["leads", "deal", id],
    queryFn: () =>
      listLeads({ opportunityId: id, accountId: deal?.accountId }),
    enabled: enrollOpen && Boolean(id),
  });

  const enrollMutation = useMutation({
    mutationFn: () => {
      const fromCommittee =
        deal?.committee?.map((c) => c.leadId).filter(Boolean) ?? [];
      const fromCompany =
        dealContactsQuery.data?.people.map((c) => c.leadId) ?? [];
      const leadIds = [...new Set([...fromCommittee, ...fromCompany])];
      if (!enrollSequenceId) throw new Error("Select a sequence");
      if (leadIds.length === 0) throw new Error("No contacts on this deal");
      return enrollInSequence(enrollSequenceId, { leadIds });
    },
    onSuccess: () => {
      setEnrollOpen(false);
      navigate(`/sequences/${enrollSequenceId}`);
    },
  });

  const addEngagement = useMutation({
    mutationFn: (input: {
      type: "note" | "email" | "call" | "meeting";
      summary: string;
    }) =>
      createEngagement({
        opportunityId: id!,
        accountId: deal?.accountId,
        type: input.type,
        summary: input.summary.trim(),
      }),
    onSuccess: () => {
      setNoteDraft("");
      setEmailDraft("");
      setCallDraft("");
      qc.invalidateQueries({ queryKey: ["engagements", id] });
      qc.invalidateQueries({ queryKey: ["opportunities", "detail", id] });
    },
  });

  const syncEmail = useMutation({
    mutationFn: async () => {
      await syncEmails({ opportunityId: id! });
      return syncOpportunityEmail(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engagements", id] });
      qc.invalidateQueries({ queryKey: ["emails"] });
    },
  });

  const stageType = stages.find((s) => s.name === stage)?.type;
  const isClosed = stageType === "won" || stageType === "lost";

  const saveCore = () => {
    if (!id) return;
    const amountNum = amount.trim() ? Number(amount) : undefined;
    const probNum = probability.trim() ? Number(probability) : undefined;
    const stageDef = stages.find((s) => s.name === stage);
    updateDeal.mutate({
      dealName: dealName.trim() || undefined,
      amount: Number.isFinite(amountNum) ? amountNum : undefined,
      probability: Number.isFinite(probNum) ? probNum : undefined,
      forecastCategory: forecastCategory || undefined,
      closeDate: closeDate || undefined,
      ownerName: ownerName.trim() || undefined,
      nextStep: nextStep.trim() || undefined,
      nextStepDueAt: nextStepDueAt || undefined,
      winLossReason: winLossReason.trim() || undefined,
      stage: stage || undefined,
      stageId: stageDef?.id,
      pipelineConfigId: dealPipeline?.pipelineConfigId,
      brief: brief.trim() || undefined,
    });
  };

  const saveMeddicc = () => {
    if (!id) return;
    const cleaned: Record<string, string> = {};
    for (const [key] of MEDDICC_FIELDS) {
      const v = (meddicc[key] ?? "").trim();
      if (v) cleaned[key] = v;
    }
    updateDeal.mutate({
      meddicc: Object.keys(cleaned).length ? cleaned : {},
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="mx-auto max-w-3xl">
        <Link
          to="/deals"
          className="mb-6 inline-flex text-sm text-brand hover:underline"
        >
          ← Back to Deals
        </Link>
        <p className="text-sm text-destructive">Deal not found.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <Link
          to="/deals"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← Deals
        </Link>
        <button
          type="button"
          className="text-xs text-red-600 hover:underline"
          onClick={() => {
            if (confirm("Delete this deal?")) {
              deleteDeal.mutate(deal.id, {
                onSuccess: () => navigate("/deals"),
              });
            }
          }}
        >
          Delete deal
        </button>
      </div>

      {/* Header */}
      <header className="mb-6 border-b border-border pb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <input
              className="w-full border-0 bg-transparent text-2xl font-bold text-foreground outline-none focus:ring-0"
              value={dealName}
              onChange={(e) => setDealName(e.target.value)}
              onBlur={saveCore}
            />
            <p className="mt-1 text-sm text-muted-foreground">
              Account:{" "}
              {deal.accountId ? (
                <Link
                  to={`/user/accounts/${deal.accountId}`}
                  className="text-brand hover:underline"
                >
                  {deal.company}
                </Link>
              ) : (
                deal.company
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={stage}
              onChange={(e) => {
                const name = e.target.value;
                setStage(name);
                const def = stages.find((s) => s.name === name);
                updateDeal.mutate({
                  stage: name,
                  stageId: def?.id,
                  pipelineConfigId: dealPipeline?.pipelineConfigId,
                  probability: def?.probability,
                  forecastCategory: def?.forecastCategory,
                });
              }}
            >
              {stages.map((s) => (
                <option key={s.id} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setEnrollOpen(true)}
              className="rounded-md border border-border px-3 py-1.5 text-sm"
            >
              Enroll
            </button>
            <Link
              to="/emails"
              className="rounded-md border border-border px-3 py-1.5 text-sm"
            >
              Emails
            </Link>
            <button
              type="button"
              onClick={saveCore}
              disabled={updateDeal.isPending}
              className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Amount</dt>
            <dd className="font-medium tabular-nums">
              {formatMoney(
                amount.trim() ? Number(amount) : deal.amount,
                deal.currency,
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Close date</dt>
            <dd>{closeDate || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Owner</dt>
            <dd>{ownerName || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Last activity</dt>
            <dd>
              {deal.lastActivityAt
                ? new Date(deal.lastActivityAt).toLocaleDateString()
                : "—"}
            </dd>
          </div>
        </dl>
      </header>

      <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
        {/* Left rail */}
        <aside className="space-y-4 text-sm">
          <section className="space-y-3">
            <h2 className="text-xs font-semibold uppercase text-muted-foreground">
              Deal fields
            </h2>
            <label className="block">
              Amount
              <input
                type="number"
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onBlur={saveCore}
              />
            </label>
            <label className="block">
              Close date
              <input
                type="date"
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                value={closeDate}
                onChange={(e) => setCloseDate(e.target.value)}
                onBlur={saveCore}
              />
            </label>
            <label className="block">
              Owner
              <input
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                onBlur={saveCore}
              />
            </label>
            <label className="block">
              Probability %
              <input
                type="number"
                min={0}
                max={100}
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                value={probability}
                onChange={(e) => setProbability(e.target.value)}
                onBlur={saveCore}
              />
            </label>
            <label className="block">
              Forecast
              <select
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                value={forecastCategory}
                onChange={(e) => setForecastCategory(e.target.value)}
                onBlur={saveCore}
              >
                <option value="">—</option>
                <option value="pipeline">Pipeline</option>
                <option value="best_case">Best case</option>
                <option value="commit">Commit</option>
                <option value="closed">Closed</option>
                <option value="omit">Omit</option>
              </select>
            </label>
            <label className="block">
              Next step
              <input
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                value={nextStep}
                onChange={(e) => setNextStep(e.target.value)}
                onBlur={saveCore}
              />
            </label>
            <label className="block">
              Next step due
              <input
                type="date"
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                value={nextStepDueAt}
                onChange={(e) => setNextStepDueAt(e.target.value)}
                onBlur={saveCore}
              />
            </label>
            {isClosed ? (
              <label className="block">
                Win / loss reason
                <textarea
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                  rows={2}
                  value={winLossReason}
                  onChange={(e) => setWinLossReason(e.target.value)}
                  onBlur={saveCore}
                />
              </label>
            ) : null}
            <label className="block">
              Description
              <textarea
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                rows={3}
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                onBlur={saveCore}
              />
            </label>
          </section>
        </aside>

        {/* Main */}
        <div className="space-y-8">
          <section>
            <h2 className="mb-3 text-sm font-semibold">Contacts on deal</h2>
            <BuyingCommittee opportunity={deal} />
          </section>

          <section>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">Activity</h2>
              <button
                type="button"
                onClick={() => syncEmail.mutate()}
                disabled={syncEmail.isPending}
                className="text-xs text-brand hover:underline disabled:opacity-50"
              >
                Sync emails
              </button>
            </div>

            <div className="mb-4 grid gap-3 sm:grid-cols-3">
              <div>
                <textarea
                  className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                  rows={2}
                  placeholder="Add a note…"
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                />
                <button
                  type="button"
                  disabled={!noteDraft.trim() || addEngagement.isPending}
                  onClick={() =>
                    addEngagement.mutate({ type: "note", summary: noteDraft })
                  }
                  className="mt-1 text-xs text-brand hover:underline disabled:opacity-50"
                >
                  Log note
                </button>
              </div>
              <div>
                <textarea
                  className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                  rows={2}
                  placeholder="Log email…"
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                />
                <button
                  type="button"
                  disabled={!emailDraft.trim() || addEngagement.isPending}
                  onClick={() =>
                    addEngagement.mutate({
                      type: "email",
                      summary: emailDraft,
                    })
                  }
                  className="mt-1 text-xs text-brand hover:underline disabled:opacity-50"
                >
                  Log email
                </button>
              </div>
              <div>
                <textarea
                  className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                  rows={2}
                  placeholder="Call notes…"
                  value={callDraft}
                  onChange={(e) => setCallDraft(e.target.value)}
                />
                <div className="mt-1 flex flex-wrap gap-3">
                  <button
                    type="button"
                    disabled={!callDraft.trim()}
                    onClick={() => {
                      void (async () => {
                        if (!id || !deal) return;
                        const { call } = await createCall({
                          opportunityId: id,
                          accountId: deal.accountId,
                          direction: "outbound",
                          status: "completed",
                          notes: callDraft.trim(),
                        });
                        await completeCall(call.callId, {
                          notes: callDraft.trim(),
                        });
                        setCallDraft("");
                        void qc.invalidateQueries({
                          queryKey: ["engagements", id],
                        });
                        void qc.invalidateQueries({ queryKey: ["calls"] });
                      })();
                    }}
                    className="text-xs text-brand hover:underline disabled:opacity-50"
                  >
                    Log call
                  </button>
                  <Link
                    to="/calls/new"
                    className="text-xs text-brand hover:underline"
                    onClick={() => void softphone.connectDevice()}
                  >
                    Call now
                  </Link>
                </div>
              </div>
            </div>

            <ul className="space-y-2 text-sm">
              {engagements.length === 0 && calendarEvents.length === 0 ? (
                <li className="text-muted-foreground">No activity yet.</li>
              ) : null}
              {engagements.map((eg) => (
                <li
                  key={eg.id}
                  className="rounded-md border border-border/80 px-3 py-2"
                >
                  <div className="flex justify-between gap-2 text-xs text-muted-foreground">
                    <span className="uppercase">{eg.type}</span>
                    <span>
                      {eg.at ? new Date(eg.at).toLocaleString() : ""}
                    </span>
                  </div>
                  <p className="mt-1">{eg.summary}</p>
                </li>
              ))}
              {calendarEvents.map((ev) => {
                const startIso = ev.start?.dateTime ?? ev.start?.date;
                return (
                  <li
                    key={ev.id}
                    className="rounded-md border border-border/80 px-3 py-2"
                  >
                    <div className="flex justify-between gap-2 text-xs text-muted-foreground">
                      <span className="uppercase">Calendar</span>
                      <span>
                        {startIso ? new Date(startIso).toLocaleString() : ""}
                      </span>
                    </div>
                    <p className="mt-1">{ev.summary || "Event"}</p>
                  </li>
                );
              })}
            </ul>
          </section>

          <section>
            <button
              type="button"
              onClick={() => setIntelOpen((o) => !o)}
              className="mb-3 text-sm font-semibold text-muted-foreground hover:text-foreground"
            >
              {intelOpen ? "▾" : "▸"} Intel (MEDDICC, research, risk)
            </button>
            {intelOpen ? (
              <div className="space-y-4 rounded-lg border border-border p-4">
                {intel ? (
                  <div className="grid gap-3 sm:grid-cols-2 text-sm">
                    <p>
                      Risk score:{" "}
                      <span className="font-medium">
                        {intel.riskScore ?? "—"}
                      </span>
                    </p>
                    <div>
                      <p className="text-xs text-muted-foreground">NBA</p>
                      <ul className="list-disc pl-4">
                        {(intel.nba ?? []).map((a) => (
                          <li key={a}>{a}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : null}
                <div className="grid gap-2 sm:grid-cols-2">
                  {MEDDICC_FIELDS.map(([key, label]) => (
                    <label key={key} className="block text-sm">
                      {label}
                      <input
                        className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1"
                        value={meddicc[key] ?? ""}
                        onChange={(e) =>
                          setMeddicc((m) => ({ ...m, [key]: e.target.value }))
                        }
                        onBlur={saveMeddicc}
                      />
                    </label>
                  ))}
                </div>
                <div>
                  <button
                    type="button"
                    disabled={researchJob.isPending}
                    onClick={() =>
                      researchJob.mutate({ opportunityId: deal.id })
                    }
                    className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50"
                  >
                    {researchJob.isPending ? "Researching…" : "Run research"}
                  </button>
                  {deal.research ? (
                    <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-muted/20 p-3 text-xs">
                      {deal.research}
                    </pre>
                  ) : null}
                </div>
              </div>
            ) : null}
          </section>
        </div>
      </div>

      {enrollOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setEnrollOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-lg border border-border bg-card p-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-3 text-sm font-semibold">Enroll in sequence</h3>
            <select
              className="mb-3 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={enrollSequenceId}
              onChange={(e) => setEnrollSequenceId(e.target.value)}
            >
              <option value="">Select sequence…</option>
              {(sequencesQuery.data?.sequences ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {enrollMutation.error ? (
              <p className="mb-2 text-xs text-red-600">
                {enrollMutation.error instanceof Error
                  ? enrollMutation.error.message
                  : "Enroll failed"}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="text-sm text-muted-foreground"
                onClick={() => setEnrollOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={enrollMutation.isPending}
                onClick={() => enrollMutation.mutate()}
                className={cn(
                  "rounded-md bg-brand px-3 py-1.5 text-sm text-brand-foreground",
                  enrollMutation.isPending && "opacity-50",
                )}
              >
                Enroll contacts
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
